import { createSelector } from 'reselect';
import { take, isEmpty } from 'lodash/fp';

// Utils
import { createShallowSelector } from '../../utils/milanoteReselect/milanoteReselect';
import { isElementOrAncestorInTrash } from '../../../common/elements/utils/elementTraversalUtils';
import { isAsyncEntityFetched } from '../../utils/services/http/asyncResource/asyncResourceUtils';
import { getTitle, getRootBoardProperty, getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { getIsTemplate, isTemplateCustomBoard } from '../../../common/templates/templateUtil';
import { isBoard } from '../../../common/elements/utils/elementTypeUtils';

import { getTemplateBoardId, matchTemplate, sortMatchedTemplates } from './boardTemplatePickerUtil';

import { getUserEnableTemplatePicker } from '../../../common/users/utils/userPropertyUtils';

import { canSave } from '../../../common/permissions/permissionUtil';

// Selectors
import { getCmsData } from '../../cms/cmsSelector';
import { getAsyncResourceEntityState } from '../../utils/services/http/asyncResource/asyncResourceSelector';
import { getCurrentBoardResourceState } from '../../element/board/boardSelector';
import { boardVisibleElementGraphSelector } from '../../element/selectors/elementGraphSelector';
import { getCurrentBoard, getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { getElements } from '../../element/selectors/elementsSelector';
import { getIsModalOpen } from '../../components/modal/activeModalSelector';
import { getIsAnnotationModeEnabled } from '../../element/drawing/annotation/store/annotationSelector';
import { getCurrentUser } from '../../user/currentUserSelector';
import { currentBoardUserPermissionsSelector } from '../../utils/permissions/elementPermissionsSelector';

// Constants
import { CmsEntities } from '../../cms/cmsConstants';
import { ResourceTypes } from '../../utils/services/http/asyncResource/asyncResourceConstants';
import { TemplateType, TemplateCollection, TemplatesMap, DefaultTemplate } from './templateTypes';

const getTemplatesCmsData = getCmsData(CmsEntities.TEMPLATES);

const getCmsTemplates = createSelector(getTemplatesCmsData, (templateData) => templateData?.templates);
const getCmsTemplateCollections = createSelector(getTemplatesCmsData, (templateData) => templateData?.collections);

const getCmsDefaultTemplates = createSelector(
    getTemplatesCmsData,
    (templateData) => templateData?.defaultTemplates || [],
);
const getCmsCommonBoardTitles = createSelector(
    getTemplatesCmsData,
    (templateData) => templateData?.commonBoardTitles || [],
);

export const getCmsTemplatesFetched = (state: any) =>
    isAsyncEntityFetched(getAsyncResourceEntityState(state, ResourceTypes.cms, CmsEntities.TEMPLATES));

const getOnboardDisableTemplatePicker = (state: any) =>
    state.getIn(['app', 'onboarding', 'metadata', 'disableTemplatePicker']);
export const getOnboardHideTemplatePicker = (state: any) =>
    state.getIn(['app', 'onboarding', 'metadata', 'hideTemplatePicker']);

export const getOnboardTemplateLandingPage = (state: any) =>
    state.getIn(['app', 'onboarding', 'metadata', 'landing_page']);
export const getOnboardSelectTemplate = (state: any) =>
    state.getIn(['app', 'onboarding', 'metadata', 'select_template']);

export const getOnboardingBoardTitle = (state: any) => state.getIn(['app', 'onboarding', 'metadata', 'board_title']);
export const getOnboardingTemplateBoardId = (state: any) =>
    state.getIn(['app', 'onboarding', 'metadata', 'templateBoardId']);

const selectCustomTemplateBoards = createSelector(getElements, (elements) =>
    elements.filter(
        (element) =>
            isBoard(element) &&
            isTemplateCustomBoard(element) &&
            !isElementOrAncestorInTrash(elements, getElementId(element)),
    ),
);

const customTemplatesSelector = createSelector(selectCustomTemplateBoards, (customTemplateBoards) => {
    const templatesMap: TemplatesMap = {};

    customTemplateBoards.forEach((element) => {
        const elementId = getElementId(element);
        const title = getTitle(element) || '';

        templatesMap[elementId] = {
            id: elementId,
            title,
            templateBoardId: elementId,
            type: TemplateType.CUSTOM,
            keywords: '',
            forceClear: true,
        };
    });

    return templatesMap;
});

export const templatesSelector = createSelector(
    getCmsTemplates,
    customTemplatesSelector,
    (builtInTemplates, customTemplates): TemplatesMap => {
        if (!builtInTemplates) return {};

        return { ...builtInTemplates, ...customTemplates };
    },
);

export const defaultTemplateListingSelector = createSelector(
    getCurrentBoard,
    getCmsDefaultTemplates,
    getCmsCommonBoardTitles,
    templatesSelector,
    getOnboardTemplateLandingPage,
    (currentBoard, defaultTemplates, commonBoardTitles, templates, landingPage): DefaultTemplate[] => {
        const currentBoardTitle = getTitle(currentBoard) || '';
        const isCommonBoardTitle = commonBoardTitles.includes(currentBoardTitle.toLowerCase());
        // don't return suggestions based on board title if a landing page param is set or the if the board title is a common one
        if (landingPage || isCommonBoardTitle) {
            return defaultTemplates;
        }

        const searchResults = Object.values(templates)
            .filter(matchTemplate(currentBoardTitle))
            .sort(sortMatchedTemplates(currentBoardTitle, defaultTemplates));

        if (isEmpty(searchResults)) return defaultTemplates;

        const firstSearchResult = searchResults[0];

        return take(5, [
            firstSearchResult,
            ...defaultTemplates.filter((template) => template.id !== firstSearchResult.id),
        ]);
    },
);

export const templateCollectionsSelector = createSelector(
    getCmsTemplateCollections,
    customTemplatesSelector,
    (templateCollections = [], customTemplates) => {
        if (!isEmpty(customTemplates)) {
            const customTemplateCollection: TemplateCollection = {
                id: '__custom-templates__',
                title: 'Your custom templates',
                templates: Object.values(customTemplates),
            };
            return [customTemplateCollection, ...templateCollections];
        }

        return templateCollections;
    },
);

const getIsDragging = (state: any) => state.getIn(['app', 'dragging', 'isDragging']);

const currentBoardVisibleChildrenIdsSelector = createShallowSelector(
    boardVisibleElementGraphSelector,
    getCurrentBoardId,
    (visibleElementGraph, visibleBoardId) => visibleElementGraph[visibleBoardId] || [],
);

// (distinct from getIsCurrentVisibleBoardFetched, so that it doesn't hide the picker while loading a template board)
const getIsCurrentBoardFetched = (state: any) => isAsyncEntityFetched(getCurrentBoardResourceState(state));

const getCurrentUserEnableTemplatePickerSelector = createSelector(getCurrentUser, (currentUser) =>
    getUserEnableTemplatePicker(currentUser),
);

export const templatePickerVisibleSelector = createSelector(
    getCurrentUserEnableTemplatePickerSelector,
    getCmsTemplatesFetched,
    currentBoardVisibleChildrenIdsSelector,
    getCurrentBoard,
    getIsCurrentBoardFetched,
    getIsDragging,
    getOnboardDisableTemplatePicker,
    getIsModalOpen,
    getIsAnnotationModeEnabled,
    currentBoardUserPermissionsSelector,
    (
        currentUserEnabledTemplatePicker,
        templatesFetched,
        currentBoardChildrenIds,
        currentBoard,
        isCurrentBoardFetched,
        isDragging,
        onboardingDisable,
        isModalOpen,
        isDrawingModeEnabled,
        permissions,
    ) =>
        currentUserEnabledTemplatePicker &&
        templatesFetched &&
        currentBoardChildrenIds.length === 0 &&
        !getIsTemplate(currentBoard) &&
        !getRootBoardProperty(currentBoard) &&
        isCurrentBoardFetched &&
        !isDragging &&
        !onboardingDisable &&
        !isModalOpen &&
        !isDrawingModeEnabled &&
        canSave(permissions),
);

export const selectedTemplateBoardSelector = createSelector(
    getElements,
    templatesSelector,
    (_: any, selectedTemplateId: string | null) => selectedTemplateId,
    (elements, templates, selectedTemplateId) => {
        if (!selectedTemplateId) return null;

        return elements.get(getTemplateBoardId(templates && templates[selectedTemplateId]));
    },
);
